import { inject, Injectable } from '@angular/core';
import { LocalstorageService } from '../../../core/services/essentials/localstorage.service';
import { LocalStorageItem} from '../../../shared/enums/app.enums';
import { map, Observable, of, tap } from 'rxjs';
import { CompanyInsightService } from './company-insight.service';

export interface SavedJobsCache {
    userId: string;
    jobIds: string[];
    savedAt: number;
}

@Injectable({ providedIn: 'root' })
export class SavedJobsCacheService {
    private readonly companyInsightService = inject(CompanyInsightService);
    private localStorageService = inject(LocalstorageService);
    private readonly CACHED_TIME_MS = 3 * 60 * 60 * 1000; // 3 hours
    private readonly now = Date.now();

    public getSavedJobsBasedOnUser(userId:string):string[] | null{
        const cache = this.localStorageService.getItem(LocalStorageItem.SAVED_JOBS_CACHE);
        if(!cache) return null;

        const cachedData:SavedJobsCache = JSON.parse(cache);

        if(cachedData.userId !== userId) return null;

        if(Date.now() - cachedData.savedAt > this.CACHED_TIME_MS) {
            this.localStorageService.removeItem(LocalStorageItem.SAVED_JOBS_CACHE);
            return null;
        }
        return cachedData.jobIds;
    }

    setSavedJobsCache(userId:string, jobIds:string[]):void{
        const cachedData:SavedJobsCache = {
            userId,
            jobIds,
            savedAt: this.now,
        }
        this.localStorageService.setItem(LocalStorageItem.SAVED_JOBS_CACHE, JSON.stringify(cachedData));
    }

    addJobId(userId: string, jobId: string): void {
        const cachedData = this.localStorageService.getItem(LocalStorageItem.SAVED_JOBS_CACHE);
        if (!cachedData) return;

        const entry: SavedJobsCache = JSON.parse(cachedData);
        if (entry.userId !== userId) return;

        // avoid duplicates
        if (!entry.jobIds.includes(jobId)) {
            entry.jobIds.push(jobId);
            entry.savedAt = Date.now();
            this.localStorageService.setItem(LocalStorageItem.SAVED_JOBS_CACHE, JSON.stringify(entry));
        }
    }

    removeJobId(userId: string, jobId: string): void {
        const cachedData = this.localStorageService.getItem(LocalStorageItem.SAVED_JOBS_CACHE);
        if (!cachedData) return;

        const entry: SavedJobsCache = JSON.parse(cachedData);
        if (entry.userId !== userId) return;

        entry.jobIds = entry.jobIds.filter(id => id !== jobId);
        entry.savedAt = Date.now();
        this.localStorageService.setItem(LocalStorageItem.SAVED_JOBS_CACHE, JSON.stringify(entry));
    }

    public getSavedJobs(userId:string): Observable<string[] | null> {
        if(!userId) return of(null);
        
        const cahcedJobIds = this.getSavedJobsBasedOnUser(userId);
        if(cahcedJobIds !== null){
            return of(cahcedJobIds)
        }
        return this.companyInsightService.getSavedJobs(userId).pipe(
            map(data => {
                const response = data[0].eventData[0].value?.map(String) ?? [];
                return response;
            }),
            tap(jobIds => {
                if (jobIds) {
                    this.setSavedJobsCache(userId, jobIds);
                }
            })
        );
    }
}